import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { LogOut, Inbox, Mail, ShoppingBag, RefreshCw, ExternalLink, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { listRecent, signOut } from "../firebase";
import useAuth from "../useAuth";
import AdminNewsPanel from "../components/AdminNewsPanel";

const TABS = [
  { key: "orders", label: "Orders", icon: ShoppingBag, collection: "orders" },
  { key: "messages", label: "Messages", icon: Inbox, collection: "messages" },
  { key: "subscribers", label: "Subscribers", icon: Mail, collection: "subscribers" },
  { key: "news", label: "News", icon: Newspaper, collection: null },
];

export default function Admin() {
  const { user, loading, admin } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("orders");
  const [items, setItems] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const active = TABS.find((t) => t.key === tab);

  const load = async () => {
    if (!admin) return;
    if (!active.collection) {
      setItems([]);
      return;
    }
    setRefreshing(true);
    try {
      const rows = await listRecent(active.collection, 100);
      setItems(rows);
    } catch (err) {
      toast.error("Couldn't load", { description: err.message });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, admin]);

  if (loading) {
    return <div className="max-w-md mx-auto px-6 py-32 text-center text-muted text-sm">Loading…</div>;
  }
  if (!user || !admin) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }

  return (
    <div data-testid="admin-page" className="relative">
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="section-num">
              <strong>·</strong> Admin desk
            </p>
            <h1 className="font-display text-display-md mt-4 text-ink tracking-tightest">
              Welcome,
              <br />
              <span className="font-italic text-coral">{user.displayName || user.email}</span>
            </h1>
          </div>
          <button
            onClick={() => signOut().then(() => toast("Signed out"))}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-bone border border-ink/15 hover:border-coral hover:text-coral text-ink text-sm font-medium transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        <div className="mt-8 flex flex-wrap gap-2.5">
          {TABS.map((t) => {
            const isActive = t.key === tab;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border transition-all ${
                  isActive
                    ? "bg-ink text-bone border-ink"
                    : "bg-bone text-ink border-ink/15 hover:border-coral hover:text-coral"
                }`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
                {isActive && t.collection && items.length > 0 && (
                  <span className="ml-1 text-bone/60 tabular-nums">{items.length}</span>
                )}
              </button>
            );
          })}
          <button
            onClick={load}
            data-testid="admin-refresh"
            className="ml-auto inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs text-muted hover:text-coral"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        {renderAdminBody({ tab, items, refreshing, active })}
      </section>
    </div>
  );
}

function renderAdminBody({ tab, items, refreshing, active }) {
  if (tab === "news") {
    return <AdminNewsPanel />;
  }
  if (items.length === 0 && !refreshing) {
    return (
      <div className="text-center py-20 text-muted">
        <active.icon className="h-10 w-10 mx-auto opacity-50" />
        <p className="mt-4">Nothing here yet.</p>
      </div>
    );
  }
  return (
    <div className="rounded-3xl md:rounded-4xl border border-ink/10 bg-boneSoft overflow-hidden">
      {tab === "orders" && <OrdersView rows={items} />}
      {tab === "messages" && <MessagesView rows={items} />}
      {tab === "subscribers" && <SubscribersView rows={items} />}
    </div>
  );
}

function timeAgo(ts) {
  if (!ts || !ts.toDate) return "—";
  const d = ts.toDate();
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function OrdersView({ rows }) {
  return (
    <>
      {/* Mobile: card list */}
      <ul className="md:hidden divide-y divide-ink/5">
        {rows.map((r) => (
          <li key={r.id} className="p-5 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-muted">When</p>
                <p className="mt-1 text-ink text-sm font-medium">{timeAgo(r.createdAt)}</p>
              </div>
              <p className="font-display text-2xl tabular-nums text-ink leading-none tracking-tightest">
                ₹{r.total}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted">Items</p>
              <p className="mt-1 text-ink/85 text-sm leading-snug">
                {(r.items || []).map((it) => `${it.qty}×${it.name}`).join(", ")}
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 text-xs">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-whatsapp/15 text-whatsapp font-medium">
                  {r.channel || "—"}
                </span>
                <span className="text-muted">{r.status || "—"}</span>
              </div>
              <div className="text-right tabular-nums">
                <span className="text-muted">Sub ₹{r.subtotal}</span>
                {r.discount > 0 && (
                  <span className="ml-2 text-sageDeep">−₹{r.discount}</span>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {/* Desktop: full table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bone/60 text-[10px] uppercase tracking-[0.22em] text-muted">
            <tr>
              <th className="text-left px-5 py-3">When</th>
              <th className="text-left px-5 py-3">Items</th>
              <th className="text-right px-5 py-3">Subtotal</th>
              <th className="text-right px-5 py-3">Discount</th>
              <th className="text-right px-5 py-3">Total</th>
              <th className="text-left px-5 py-3">Channel</th>
              <th className="text-left px-5 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-bone/50">
                <td className="px-5 py-4 text-ink/80 whitespace-nowrap">{timeAgo(r.createdAt)}</td>
                <td className="px-5 py-4 text-ink/85">
                  {(r.items || []).map((it) => `${it.qty}×${it.name}`).join(", ")}
                </td>
                <td className="px-5 py-4 text-right tabular-nums text-ink/70">₹{r.subtotal}</td>
                <td className="px-5 py-4 text-right tabular-nums text-sageDeep">
                  {r.discount ? `−₹${r.discount}` : "—"}
                </td>
                <td className="px-5 py-4 text-right tabular-nums font-medium text-ink">₹{r.total}</td>
                <td className="px-5 py-4">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-whatsapp/15 text-whatsapp text-xs font-medium">
                    {r.channel || "—"}
                  </span>
                </td>
                <td className="px-5 py-4 text-ink/70 text-xs">{r.status || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function MessagesView({ rows }) {
  return (
    <>
      {/* Mobile: card list */}
      <ul className="md:hidden divide-y divide-ink/5">
        {rows.map((r) => (
          <li key={r.id} className="p-5 space-y-2.5">
            <div className="flex items-center justify-between gap-3">
              <p className="text-ink font-medium">{r.name}</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted">{timeAgo(r.createdAt)}</p>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <a href={`tel:+91${(r.phone || "").replace(/\s/g, "")}`} className="link-underline text-coral">
                {r.phone}
              </a>
              {r.email && <span className="text-muted">·</span>}
              {r.email && <span className="text-ink/70">{r.email}</span>}
            </div>
            <p className="text-ink/75 text-sm leading-relaxed">{r.message}</p>
          </li>
        ))}
      </ul>

      {/* Desktop: table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bone/60 text-[10px] uppercase tracking-[0.22em] text-muted">
            <tr>
              <th className="text-left px-5 py-3">When</th>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Phone</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Message</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-bone/50 align-top">
                <td className="px-5 py-4 text-ink/80 whitespace-nowrap">{timeAgo(r.createdAt)}</td>
                <td className="px-5 py-4 text-ink font-medium">{r.name}</td>
                <td className="px-5 py-4 text-ink/85">
                  <a href={`tel:+91${(r.phone || "").replace(/\s/g, "")}`} className="link-underline">
                    {r.phone}
                  </a>
                </td>
                <td className="px-5 py-4 text-ink/75">{r.email || "—"}</td>
                <td className="px-5 py-4 text-ink/75 max-w-md">{r.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function SubscribersView({ rows }) {
  return (
    <>
      {/* Mobile: card list */}
      <ul className="md:hidden divide-y divide-ink/5">
        {rows.map((r) => (
          <li key={r.id} className="p-5 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-ink font-medium truncate">{r.email}</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted mt-1">
                {timeAgo(r.createdAt)} · {r.source}
              </p>
            </div>
            <a
              href={`mailto:${r.email}`}
              className="inline-flex items-center gap-1.5 text-coral text-xs flex-shrink-0"
            >
              Email <ExternalLink className="h-3 w-3" />
            </a>
          </li>
        ))}
      </ul>

      {/* Desktop: table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bone/60 text-[10px] uppercase tracking-[0.22em] text-muted">
            <tr>
              <th className="text-left px-5 py-3">When</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Source</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-bone/50">
                <td className="px-5 py-4 text-ink/80 whitespace-nowrap">{timeAgo(r.createdAt)}</td>
                <td className="px-5 py-4 text-ink font-medium">{r.email}</td>
                <td className="px-5 py-4 text-ink/70 text-xs uppercase tracking-[0.18em]">{r.source}</td>
                <td className="px-5 py-4 text-right">
                  <a
                    href={`mailto:${r.email}`}
                    className="link-underline inline-flex items-center gap-1.5 text-coral text-xs"
                  >
                    Email <ExternalLink className="h-3 w-3" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
